package services.http

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import it.neckar.auth.rest.messages.JwtRefreshTokenResponse
import it.neckar.auth.rest.messages.LoginRequest
import it.neckar.auth.rest.messages.LoginResponse
import it.neckar.ktor.client.FetchOutcome
import it.neckar.ktor.client.bearerAuth
import it.neckar.ktor.client.featureFlagsHeader
import it.neckar.ktor.client.get
import it.neckar.ktor.client.post
import it.neckar.ktor.client.postJson
import it.neckar.ktor.client.safeTryFetch
import it.neckar.ktor.client.tryFetch
import it.neckar.lizergy.model.company.PlannerCompanyInformation
import it.neckar.lizergy.model.company.user.UserInformation
import it.neckar.rest.jwt.JwtToken
import it.neckar.user.UserLoginName
import it.neckar.user.UserPassword
import services.auth.http.AddNewCompanyRequest
import services.auth.http.AddNewCompanyResponse
import services.auth.http.AddNewUserRequest
import services.auth.http.AddNewUserResponse
import services.auth.http.ChangeCompanyInfoRequest
import services.auth.http.ChangePasswordRequest
import services.auth.http.ChangePasswordResponse
import services.auth.http.ChangeUserInfoRequest
import services.auth.http.ChangeUserInfoResponse
import services.auth.http.ChangedCompanyInfoResponse
import services.auth.http.DeleteUserRequest
import services.auth.http.DeletedUserResponse
import services.auth.http.FetchUsersAndCompaniesResponse

/**
 * Service that manages the authentication
 *
 * TODO: Split authentication and user/company management
 */
class AuthenticationClientService(
  val httpClientWithAuth: HttpClient,
  /**
   * HTTP client that does not have any authentication configured.
   */
  val httpClientNoAuth: HttpClient,
  val urlSupport: PlannerUrlSupport,
) {

  /**
   * Login the current user
   */
  suspend fun login(loginName: UserLoginName, password: UserPassword, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): FetchOutcome<LoginResponse> {
    return tryFetch {
      httpClientNoAuth
        .postJson(urlSupport.auth.login, LoginRequest(loginName, password)) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(LoginResponse.serializer())
    }
  }

  /**
   * Refreshes the access token (using the refresh token)
   *
   * Throws every failure other than a 401, the network failure included: the refresh token may still be valid, and [JwtRefreshTokenResponse.InvalidRefreshToken] would log the user out.
   */
  suspend fun refreshAccessToken(refreshToken: JwtToken, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): JwtRefreshTokenResponse {
    val outcome = tryFetch {
      httpClientNoAuth
        .post(urlSupport.auth.refreshAccessToken) {
          featureFlagsHeader()
          bearerAuth(refreshToken)
          additionalRequestConfiguration()
        }.plannerBody(JwtRefreshTokenResponse.serializer())
    }

    return when (outcome) {
      is FetchOutcome.Delivered -> outcome.value
      FetchOutcome.Unauthorized -> JwtRefreshTokenResponse.InvalidRefreshToken
      is FetchOutcome.Unreachable -> throw outcome.cause
    }
  }

  suspend fun addNewUser(newUserInformation: UserInformation, password: UserPassword, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): AddNewUserResponse {
    return safeTryFetch(AddNewUserResponse.bad(AddNewUserResponse.Failed.ErrorType.FailToFetch)) {
      httpClientWithAuth
        .postJson(urlSupport.auth.addNewUser, AddNewUserRequest(newUserInformation, password)) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(AddNewUserResponse.serializer())
    }
  }

  suspend fun changeUserInfo(updatedUserInformation: UserInformation, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): ChangeUserInfoResponse {
    return safeTryFetch(ChangeUserInfoResponse.bad(ChangeUserInfoResponse.Failed.ErrorType.FailToFetch)) {
      httpClientWithAuth
        .postJson(urlSupport.auth.changeUserInfo, ChangeUserInfoRequest(updatedUserInformation)) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(ChangeUserInfoResponse.serializer())
    }
  }

  suspend fun deleteUser(user: UserInformation, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): DeletedUserResponse {
    return safeTryFetch(DeletedUserResponse.bad(DeletedUserResponse.Failed.ErrorType.FailToFetch)) {
      httpClientWithAuth
        .postJson(urlSupport.auth.deleteUser, DeleteUserRequest(user)) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(DeletedUserResponse.serializer())
    }
  }

  /**
   * Changes the password for the given user
   */
  suspend fun changePassword(loginName: UserLoginName, /*oldPassword: UserPassword,*/ newPassword: UserPassword, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): ChangePasswordResponse {
    return safeTryFetch(ChangePasswordResponse.bad(ChangePasswordResponse.Failed.ErrorType.FailToFetch)) {
      httpClientWithAuth
        .postJson(urlSupport.auth.changePassword, ChangePasswordRequest(loginName, /*oldPassword,*/ newPassword)) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(ChangePasswordResponse.serializer())
    }
  }

  suspend fun addNewCompany(newCompanyInformation: PlannerCompanyInformation, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): AddNewCompanyResponse {
    return safeTryFetch(AddNewCompanyResponse.bad(AddNewCompanyResponse.Failed.ErrorType.FailToFetch)) {
      httpClientWithAuth
        .postJson(urlSupport.auth.addNewCompany, AddNewCompanyRequest(newCompanyInformation)) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(AddNewCompanyResponse.serializer())
    }
  }

  suspend fun changeCompanyInfo(updatedCompanyInformation: PlannerCompanyInformation, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): ChangedCompanyInfoResponse {
    return safeTryFetch(ChangedCompanyInfoResponse.bad(ChangedCompanyInfoResponse.Failed.ErrorType.FailToFetch)) {
      httpClientWithAuth
        .postJson(urlSupport.auth.changeCompanyInfo, ChangeCompanyInfoRequest(updatedCompanyInformation)) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(ChangedCompanyInfoResponse.serializer())
    }
  }

  suspend fun fetchUsersAndCompanies(additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): FetchUsersAndCompaniesResponse {
    return safeTryFetch(FetchUsersAndCompaniesResponse.bad()) {
      httpClientWithAuth
        .get(urlSupport.auth.listUsersAndCompanies) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(FetchUsersAndCompaniesResponse.serializer())
    }
  }
}
